import React, { FunctionComponent, useState } from 'react';
import { FabChecklist } from '@campfire/fab-checklist';
import { AppBar as MuiAppBar, Box, Button } from '@material-ui/core';
import pluralize from 'pluralize';
import { AccessTimeOutlined } from '@material-ui/icons';
import SvgIcon from '@material-ui/core/SvgIcon';
import Toolbar from '@material-ui/core/Toolbar';
import IconButton from '@material-ui/core/IconButton';
import MenuIcon from '@material-ui/icons/Menu';
import { DateTime, Interval } from 'luxon';

import { useCampfireTheme } from '../../../theme/useCampfireTheme';
import { AppBarAvatarMenu } from './AppBarAvatarMenu';
import { CampfireHeader } from '../../components';
import { useOnboardingContext } from '../../onboarding/useOnboardingContext';
import { AvailabilityCenter } from '../../components/AvailabilityCenter/AvailabilityCenter';
import { useOrgInfo } from '../../auth/use-org-info/use-org-info';
import { useUser } from '../../auth/useUser';
import { TrialUpgradeDialog } from './TrialUpgradeDialog';
import { TrialSetupDialog, useTenantSetupContext } from '../../tenant-setup';

function SettingsSparkIcon(props: React.ComponentProps<typeof SvgIcon>) {
  return (
    <SvgIcon {...props} viewBox='0 0 24 24'>
      <path fill='none' d='M0 0h24v24H0z' />
      <path
        fill='none'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
        d='M11.992 21c-.728-.003-1.455-.442-1.667-1.317a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37 1 .608 2.296.07 2.572-1.065.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c.882.214 1.32.95 1.317 1.684'
      />
      <path
        fill='none'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
        d='M9 12a3 3 0 106 0 3 3 0 00-6 0'
      />
      <path
        fill='none'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
        d='M19 22.5a4.75 4.75 0 013.5-3.5 4.75 4.75 0 01-3.5-3.5 4.75 4.75 0 01-3.5 3.5 4.75 4.75 0 013.5 3.5'
      />
    </SvgIcon>
  );
}

interface Props {
  isDrawerOpen: boolean;
  toggleDrawerOpen: () => void;
}

const CampfireAppBar: FunctionComponent<Props> = ({ toggleDrawerOpen, isDrawerOpen }) => {
  const { theme, isMobile } = useCampfireTheme();
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const { isSetupDialogOpen, closeSetupDialog, openSetupDialog } = useTenantSetupContext();

  const FabChecklistWrapper = () => {
    const { currentOnboardingStep, finishedOnboarding } = useOnboardingContext();
    return finishedOnboarding === false ? (
      <FabChecklist isMobile={isMobile} currentStep={currentOnboardingStep} />
    ) : null;
  };

  const orgInfo = useOrgInfo();
  const {
    user: { userIdentityService },
  } = useUser();

  const isBillingPriviledged = userIdentityService.hasVmGlobalRule('manage-billing');
  const isAdmin = userIdentityService.isVmAdmin;
  const expiredDate = orgInfo?.dateDeactivated;
  const remainingDays = expiredDate
    ? (() => {
        try {
          const { days } = Interval.fromDateTimes(DateTime.local(), expiredDate).toDuration('days');
          return Number.isFinite(days) ? Math.round(days) : undefined;
        } catch {
          return undefined;
        }
      })()
    : undefined;

  return (
    <MuiAppBar
      position='relative'
      style={{
        backgroundColor: '#423951',
        color: '#ffffff',
        boxShadow: 'none',
      }}
    >
      <Toolbar style={{ paddingLeft: 24, paddingRight: 8, minHeight: theme.appShell.menu.height }}>
        {isMobile || !isDrawerOpen ? (
          <IconButton
            color='inherit'
            onClick={() => {
              toggleDrawerOpen();
            }}
            style={{ marginLeft: '-16px' }}
          >
            <MenuIcon />
          </IconButton>
        ) : null}
        <CampfireHeader condensed />

        <div style={{ flex: 1 }} />

        <FabChecklistWrapper />
        <Box display='flex' pl='12px' alignItems='center' style={{ gap: 12 }}>
          <Box>
            {isBillingPriviledged && expiredDate && (
              <Button
                variant='contained'
                startIcon={<AccessTimeOutlined style={{ marginBottom: '2px' }} />}
                color='primary'
                style={{ color: 'white', textTransform: 'none', fontSize: '13px' }}
                onClick={() => setUpgradeDialogOpen(true)}
              >
                {pluralize('days', remainingDays, true)} left
              </Button>
            )}
          </Box>
          {isAdmin && orgInfo?.inTrial && (
            <Button
              variant='outlined'
              startIcon={<SettingsSparkIcon />}
              style={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)', textTransform: 'none', fontSize: '13px' }}
              onClick={openSetupDialog}
            >
              Setup
            </Button>
          )}
          <AvailabilityCenter />
          <AppBarAvatarMenu />
        </Box>
      </Toolbar>
      {remainingDays && (
        <TrialUpgradeDialog
          open={upgradeDialogOpen}
          onClose={() => setUpgradeDialogOpen(false)}
          remainingDays={remainingDays}
        />
      )}
      <TrialSetupDialog open={isSetupDialogOpen} onClose={closeSetupDialog} />
    </MuiAppBar>
  );
};

export const AppBar = CampfireAppBar;
